import { create } from 'zustand'
import type { User as SupabaseUser } from '@supabase/supabase-js'

interface AuthUser {
  uid: string
  email: string | null
  displayName: string | null
  photoURL: string | null
  emailVerified: boolean
  roles: string[]
}

interface AuthState {
  auth: {
    user: AuthUser | null
    supabaseUser: SupabaseUser | null
    isLoading: boolean
    roles: string[]
    setUser: (user: AuthUser | null) => void
    setSupabaseUser: (user: SupabaseUser | null) => void
    setRoles: (roles: string[]) => void
    setIsLoading: (isLoading: boolean) => void
    reset: () => void
  }
}

export const useAuthStore = create<AuthState>()((set) => ({
  auth: {
    user: null,
    supabaseUser: null,
    isLoading: true,
    roles: [],
    setUser: (user) =>
      set((state) => ({ ...state, auth: { ...state.auth, user } })),
    setSupabaseUser: (supabaseUser) =>
      set((state) => {
        const user = supabaseUser
          ? {
              uid: supabaseUser.id,
              email: supabaseUser.email ?? null,
              displayName: supabaseUser.user_metadata?.display_name ?? null,
              photoURL: supabaseUser.user_metadata?.avatar_url ?? null,
              emailVerified: !!supabaseUser.email_confirmed_at,
              roles: state.auth.roles,
            }
          : null
        return { ...state, auth: { ...state.auth, supabaseUser, user } }
      }),
    setRoles: (roles) =>
      set((state) => ({
        ...state,
        auth: {
          ...state.auth,
          roles,
          user: state.auth.user
            ? { ...state.auth.user, roles }
            : null,
        },
      })),
    setIsLoading: (isLoading) =>
      set((state) => ({ ...state, auth: { ...state.auth, isLoading } })),
    reset: () =>
      set((state) => ({
        ...state,
        auth: {
          ...state.auth,
          user: null,
          supabaseUser: null,
          isLoading: false,
          roles: [],
        },
      })),
  },
}))
